import {
  type ExecutionEngineClient,
  type PortfolioExecutionFeedOperation,
  type PortfolioExecutionFeedSnapshot,
} from "@repo/execution-engine-client";
import { useEffect, useMemo, useRef, useState } from "react";
import { getBrowserExecutionEngineClient } from "~/lib/execution-engine/browser-client";
import {
  type ResilientReconnectSignals,
  useResilientLiveSubscription,
} from "./resilient-live-subscription";

type PortfolioExecutionFeedOperationSummary =
  PortfolioExecutionFeedSnapshot["operations"][number];

export type PortfolioExecutionFeedState = {
  // "disconnected" is non-terminal: the socket gave up its fast reconnect
  // budget but a slow recovery loop keeps trying, so the feed never dies.
  readonly status: "idle" | "connecting" | "live" | "disconnected";
  readonly snapshot: PortfolioExecutionFeedSnapshot | null;
  readonly operations: readonly PortfolioExecutionFeedOperationSummary[];
  readonly lastSequence: number;
  readonly error: string | null;
};

export type UsePortfolioExecutionFeedOptions = {
  readonly client?: ExecutionEngineClient | null;
  readonly token?: string | null;
  readonly WebSocketImpl?: Parameters<
    ExecutionEngineClient["subscribePortfolioExecutionFeed"]
  >[1]["WebSocketImpl"];
  /** Injectable re-arm signals (tests); defaults to browser online/visibility. */
  readonly signals?: ResilientReconnectSignals;
};

const initialState: PortfolioExecutionFeedState = {
  status: "idle",
  snapshot: null,
  operations: [],
  lastSequence: 0,
  error: null,
};

const mergeOperation = (
  operations: readonly PortfolioExecutionFeedOperationSummary[],
  update: PortfolioExecutionFeedOperation,
): readonly PortfolioExecutionFeedOperationSummary[] => {
  const next = new Map(
    operations.map((operation) => [operation.operationId, operation]),
  );
  next.set(update.operation.operationId, update.operation);

  return Array.from(next.values()).sort(
    (left, right) =>
      Date.parse(right.updatedAt) - Date.parse(left.updatedAt) ||
      Date.parse(right.createdAt) - Date.parse(left.createdAt),
  );
};

export function usePortfolioExecutionFeed(
  portfolioId: string | null | undefined,
  options: UsePortfolioExecutionFeedOptions = {},
): PortfolioExecutionFeedState & {
  readonly isConfigured: boolean;
} {
  const configuredClient = options.client;
  const token = options.token ?? null;
  const WebSocketImpl = options.WebSocketImpl;
  const signals = options.signals;
  const lastSequenceRef = useRef(0);
  const [state, setState] = useState<PortfolioExecutionFeedState>(initialState);

  const client = useMemo(
    () =>
      configuredClient === undefined
        ? getBrowserExecutionEngineClient()
        : configuredClient,
    [configuredClient],
  );

  const scopeKey = client && portfolioId ? portfolioId : null;

  useEffect(() => {
    lastSequenceRef.current = state.lastSequence;
  }, [state.lastSequence]);

  useEffect(() => {
    lastSequenceRef.current = 0;
    setState(
      scopeKey ? { ...initialState, status: "connecting" } : initialState,
    );
  }, [scopeKey]);

  useResilientLiveSubscription({
    enabled: scopeKey !== null,
    scopeKey,
    ...(signals ? { signals } : {}),
    getLastSequence: () => lastSequenceRef.current,
    onStatus: (connectionStatus, error) => {
      setState((current) => {
        if (connectionStatus === "disconnected") {
          return { ...current, status: "disconnected", error: null };
        }
        if (connectionStatus === "connecting") {
          return { ...current, status: "connecting", error };
        }
        return current;
      });
    },
    open: async (context) => {
      if (!client || !portfolioId) {
        return;
      }

      // A caller-supplied token is only a hint for the very first connect; every
      // reconnect re-fetches a fresh, unexpired live descriptor.
      const descriptorToken =
        context.isInitialConnect && token
          ? token
          : ((await client.getPortfolioExecutionFeedLive(portfolioId)).token ??
            null);
      if (!context.isCurrent()) {
        return;
      }

      const subscription = client.subscribePortfolioExecutionFeed(portfolioId, {
        lastSequence: context.lastSequence,
        token: descriptorToken,
        ...(WebSocketImpl ? { WebSocketImpl } : {}),
        onSnapshot: (snapshot) => {
          context.noteActivity();
          setState((current) => ({
            ...current,
            status: "live",
            snapshot,
            operations: snapshot.operations,
            lastSequence: snapshot.sequence,
            error: null,
          }));
        },
        onOperation: (operation) => {
          context.noteActivity();
          const operationSequence = operation.sequence;
          setState((current) => {
            if (operationSequence < current.lastSequence) {
              return current;
            }
            return {
              ...current,
              status: "live",
              operations: mergeOperation(current.operations, operation),
              lastSequence: operationSequence,
              error: null,
            };
          });
        },
        onReplayOverflow: () => {},
        onError: (error) => {
          context.handleError(error);
        },
        onClose: () => {
          context.handleClose();
        },
      });

      context.bindSubscription({ close: () => subscription.close() });
    },
  });

  return {
    ...state,
    isConfigured: client !== null,
  };
}
